import { useEffect, useState } from 'react'

import { type Filter } from '@rewardops/api'
import queryBuilder from '@rewardops/api/src/query-builder'
import {
  useMember,
  useMemberTags,
  useSegmentCode,
} from '@rewardops/auth/src/client'
import { useLocale } from '@rewardops/ui'
import { type ResponseItem } from '@rewardops/utils'

import {
  RETAILERS_SEARCH_QUERY_MIN_LENGTH,
  SEARCH_QUERY_MIN_LENGTH,
} from '~/utils/sort-utils'
import { useApi } from './use-api'

export const usePaginatedItems = (
  filter: Filter,
  page: number,
  pageNumber: number,
) => {
  const [error, setError] = useState(false)
  const [items, setItems] = useState([] as ResponseItem[])
  const member = useMember()
  const segmentCode = useSegmentCode()
  const memberTags = useMemberTags()
  const { locale } = useLocale()
  const { api, tenant } = useApi()

  const search_query_min_length = filter?.isAffiliate
    ? RETAILERS_SEARCH_QUERY_MIN_LENGTH
    : SEARCH_QUERY_MIN_LENGTH
  const shouldFetch = !filter?.q || filter.q.length >= search_query_min_length

  const {
    data: itemResponse,
    isLoading: queryLoading,
    error: itemsError,
  } = api.items.getAll.useQuery(
    queryBuilder.items.getAllWithFilter(
      {
        categoryCode: filter.categoryCode,
        brands: filter.brands,
        categories: filter.categories,
        sort: filter.sort,
        isAffiliate: filter?.isAffiliate,
        isPromotion: filter?.isPromotion,
        promotionLabel: filter?.promotionLabel,
        isAllItems: filter?.isAllItems ?? false,
        q: filter?.q,
        startsWith: filter?.startsWith,
        minPrice:
          filter?.onlyItemsWithinBalance && member?.points
            ? 0
            : filter?.minPrice,
        maxPrice:
          filter?.onlyItemsWithinBalance && member?.points
            ? member.points
            : filter?.maxPrice,
        earnRateCashExpressions: filter?.earnRateCashExpressions ?? [],
      },
      tenant,
      {
        segment_code: segmentCode,
        accept_language: locale,
        ...(memberTags && { member_tags: memberTags }),
        page,
      },
      page,
      pageNumber,
    ),
    {
      enabled: shouldFetch, // Control whether the query runs
    },
  )

  const loading = shouldFetch && queryLoading

  useEffect(() => {
    setError(false)

    if (loading) {
      setItems([])
    } else if (itemResponse && !('error' in itemResponse.result)) {
      if (itemsError) {
        setError(true)
        console.error(itemsError)
      } else {
        setItems([...itemResponse.result.items])
      }
    }
  }, [itemResponse, itemsError])

  return {
    loading,
    error,
    items,
    totalItems: itemResponse?.pagination?.count,
  }
}
